
import { onMounted, reactive, ref, watch } from "vue";
import VueDrawingCanvas from "vue-drawing-canvas";
import _ from "lodash";
import imageCompression from "browser-image-compression";
import { useTimeoutFn } from "@vueuse/core";

export default {
  props: {
    modelValue: String,
    width: Number,
    height: Number,
    buttons: {
      type: Array,
      required: false,
      default() {
        return [
          "dash",
          "line",
          "reset",
          "undo",
          "redo",
          "rotateLeft",
          "rotateRight"
        ];
      }
    }
  },
  emits: ["update:modelValue"],
  components: {
    VueDrawingCanvas
  },
  setup(props: object, context: { emit: Function }) {
    const image = (props as { modelValue: string }).modelValue;

    const imageSize = reactive<Record<"width" | "height", number | null>>({
      width: (props as { width?: number; height?: number }).width ?? null,
      height: (props as { width?: number; height?: number }).height ?? null
    });

    onMounted(async () => {
      if (imageSize.width && imageSize.height) {
        return;
      }

      const imgHtml = await imageCompression.loadImage(image);
      imageSize.width = imgHtml.width;
      imageSize.height = imgHtml.height;
    });

    const drawingCanvasContainer = ref(),
      drawingCanvasContainerSize = ref<{
        width: number;
        height: number;
      } | null>(null);

    watch(
      () => drawingCanvasContainer.value,
      v => {
        if (v) {
          drawingCanvasContainerSize.value = {
            width: v.clientWidth,
            height: v.clientHeight
          };
        }
      },
      { immediate: true }
    );

    const strokeType = ref<"dash" | "line">("dash");

    const drawingCanvas = ref();

    const editedImage = ref("");

    const {
      start: startModelUpdatingTimer,
      stop: stopModelUpdatingTimer
    } = useTimeoutFn(
      () => context.emit("update:modelValue", editedImage.value),
      300,
      {
        immediate: false
      }
    );

    watch(
      () => editedImage.value,
      v => {
        stopModelUpdatingTimer();

        if (drawingCanvas.value?.isEmpty()) {
          context.emit("update:modelValue", image);
        } else {
          startModelUpdatingTimer();
        }
      }
    );

    return {
      editedImage,
      image,
      strokeType,
      drawingCanvas,
      drawingCanvasContainer,
      drawingCanvasContainerSize,
      imageSize,
      ..._.pick(_, "find"),
      ...props
    };
  }
};
